import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { shouldDeferCartSideEffects } from '@theme/cart-add-perf';
import { sectionRenderer } from '@theme/section-renderer';

/** Shop Pay installments #shopify-installments-cta: close mini cart (one composedPath pass; early exit when drawer found). */
function closeOpenCartDrawerFromEvent(/** @type {Event} */ event) {
  const path = event.composedPath?.() ?? [];
  let sawCta = false;

  for (let i = 0; i < path.length; i++) {
    const node = path[i];
    if (!(node instanceof Element)) continue;

    if (
      node.id === 'shopify-installments-cta' ||
      node.getAttribute('data-testid') === 'shopify-installments-cta'
    ) {
      sawCta = true;
    }

    if (
      sawCta &&
      node.tagName === 'CART-DRAWER-COMPONENT' &&
      node.querySelector('dialog')?.open
    ) {
      /** @type {HTMLElement & { close: () => void }} */ (node).close();
      return;
    }
  }

  if (!sawCta) return;

  for (const el of document.querySelectorAll('cart-drawer-component')) {
    if (el.querySelector('dialog')?.open) {
      /** @type {HTMLElement & { close: () => void }} */ (el).close();
      return;
    }
  }
}

document.addEventListener('click', closeOpenCartDrawerFromEvent, { capture: true });

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener('click', this.handleContinueShoppingClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener('click', this.handleContinueShoppingClick);
  }

  /**
   * Close drawer when cart becomes empty after a remove or quantity update.
   * cart:update is dispatched by cart-items-component after section re-render with itemCount.
   */
  #handleCartUpdate = (/** @type {any} */ event) => {
    this.open(event);
  };

  async open(/** @type {any} */ event) {
    if (shouldDeferCartSideEffects(event)) return;

    const cartItems = /** @type {HTMLElement | null} */ (this.querySelector('cart-items-component'));
    const sectionId = cartItems?.dataset?.sectionId;
    const detail = /** @type {any} */ (event)?.detail;
    const data = detail?.data;
    const isLiveTvAdd = data?.source === 'live-tv-card-forms';
    const isNotAllowedSource =
      data?.source &&
      data.source !== 'product-form-component' &&
      data.source !== 'quick-add' &&
      data.source !== 'cart-items-component' &&
      data.source !== 'live-tv-card-forms';

    if (isNotAllowedSource) {
      window.location.href = Theme.routes.cart_url;
      return;
    };

    const canShow = !!this.querySelector('.cart-drawer__summary');
    const isAddAction = data?.action === 'add';
    const sectionsHtml = sectionId ? data?.sections?.[sectionId] : undefined;
    // Follow-up add event already carrying HTML — morph only, drawer already open.
    const isSectionFollowUp = isAddAction && !!sectionsHtml && this.querySelector('dialog')?.open;

    if (isSectionFollowUp) {
      return;
    }

    // Perceived performance: open on add before mini-cart HTML finishes loading.
    // Live TV waits until section HTML is present (or fetched below) before opening.
    if (canShow && isAddAction && !isLiveTvAdd) {
      this.showDialog();
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    }

    if (sectionId && !sectionsHtml) {
      try {
        await sectionRenderer.renderSection(sectionId, { cache: false });
      } catch (_) {
        // Still keep drawer open if section update fails
      }
    }

    if (canShow && isAddAction && isLiveTvAdd) {
      this.showDialog();
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    }

    if (canShow && !isAddAction) {
      this.showDialog();
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    }
  }

  close() {
    this.closeDialog();
  }
  handleContinueShoppingClick = (/** @type {any} */ event) => {
    if (event.target.classList.contains('cart-items__empty-button') || event.target.closest('.cart-items__empty-button')) {
      this.closeDialog();
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
